import React, { useEffect } from 'react';
import { Nav } from './nav.tsx';
import { Footer } from './footer.tsx';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';

type ParlaFunctions = {
  load: () => void;
  [key: `popup${number}`]: () => void;
}

type ParlaDoc = Document & {
  parla: ParlaFunctions;
}

export function Why() {
    const { lang } = useParams();
    const { t } = useTranslation();
    const baseURL = "https://www.parlaretail.com";
    useEffect(() => {
        if (lang) {
            i18next.changeLanguage(lang);
        }
    }, [lang]);

    return (
        <>
            <Helmet key={lang}>
                {/* General Meta */}
                <meta charSet="utf-8" />
                <meta http-equiv="X-UA-Compatible" content="IE=edge" />
                <title>{t('why.meta.title')}</title>
                <meta name="description" content={t('why.meta.description')} />
                <meta name="keywords" content={t('why.meta.keywords')} />
                <meta name="viewport" content="width=device-width, initial-scale=1" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={t('why.meta.title')} />
                <meta property="og:description" content={t('why.meta.description')} />
                <meta property="og:url" content={`${baseURL}/${lang}/`} />
                <meta property="og:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={t('why.meta.title')} />
                <meta name="twitter:description" content={t('why.meta.description')} />
                <meta name="twitter:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Hreflang for Language Versioning */}
                <link rel="alternate" hrefLang="en" href={`${baseURL}/en/`} />
                <link rel="alternate" hrefLang="es" href={`${baseURL}/es/`} />

                {/* Favicon */}
                <link rel="icon" type="image/png" href={`${baseURL}/img/parla-fav.png`} />
            </Helmet>
            <Nav />
            <div className="page">
                {/* Hero Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50">
                                    <div className="pb10 t-color-2 f70 mf40">{t('why.hero.title')}</div>
                                    <div className="description f20">{t('why.hero.description')}</div>
                                    <div className="btn-wrap">
                                        <button className="btn btn-dark" onClick={() => (document as ParlaDoc).parla.popup33()}>{t('why.hero.book_demo')}</button>
                                    </div>
                                </div>
                                <div className="w50 v-center">
                                    <img
                                        src="/img/why/Parla+homepage+hero+appliance+images.png"
                                        alt={t('why.hero.image_alt')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Conversion Section */}
                <div className="section section-column mt50">
                    <div className="row f60 mf40 t-center t-color-2 p20">{t('why.conversion.title')}</div>
                    <div className="text-info-panel w80">
                        <div className="item">
                            <div className="value t-color-1">{t('why.conversion.sales_conversion')}</div>
                            <div className="description t-color-1">{t('why.conversion.sales_conversion_description')}</div>
                        </div>
                        <div className="item">
                            <div className="value t-color-1">{t('why.conversion.higher_aov')}</div>
                            <div className="description t-color-1">{t('why.conversion.higher_aov_description')}</div>
                        </div>
                    </div>
                </div>

                {/* Retail Channels Section */}
                <div className="section-column">
                    <div className="row f70 t-center t-color-2 p20">{t('why.retail_channels.title')}</div>
                    <div className="text-info-panel w80 p20">{t('why.retail_channels.subtitle')}</div>
                    <div className="case-study-wrap pt20 w80">
                        {/* Brand 1 */}
                        <div className="card-case-study mp0 mb20 wmob">
                            <img src="/img/why/store-r2-KCKS.jpg" alt={t('why.retail_channels.brand1.image_alt')} />
                            <div className="sector">{t('why.retail_channels.brand1.title')}</div>
                            <div className="title">{t('why.retail_channels.brand1.subtitle')}</div>
                            <span className="subtitle">{t('why.retail_channels.brand1.description')}</span>
                        </div>
                        {/* Brand 2 */}
                        <div className="card-case-study pr20 mp0 mb20 wmob">
                            <img src="/img/why/troubadour-1-uuc59nu8-2021-07-09.jpg" alt={t('why.retail_channels.brand2.image_alt')} />
                            <div className="sector">{t('why.retail_channels.brand2.title')}</div>
                            <div className="title">{t('why.retail_channels.brand2.subtitle')}</div>
                            <span className="subtitle">{t('why.retail_channels.brand2.description')}</span>
                        </div>
                        {/* Brand 3 */}
                        <div className="card-case-study mp0 mb20 wmob">
                            <img src="/img/why/Saty_Pratha_HNDSM_00747_1512x.jpg" alt={t('why.retail_channels.brand3.image_alt')} />
                            <div className="sector">{t('why.retail_channels.brand3.title')}</div>
                            <div className="title">{t('why.retail_channels.brand3.subtitle')}</div>
                            <span className="subtitle">{t('why.retail_channels.brand3.description')}</span>
                        </div>
                    </div>
                </div >

                {/* Why Choose Parla Section */}
                < div className="section-column" >
                    <div className="row f70 t-center t-color-2 p20">{t('why.why_choose.title')}</div>
                    <div className="text-info-panel w80 p20">{t('why.why_choose.description')}</div>
                    <div className="card-holder w80 ptb-50">
                        <div className="row">
                            {/* Card 1 */}
                            <div className="card-why t-center p20 w80 ">
                                <i className="fal fa-lightbulb-on f50 t-color-2 p20"></i>
                                <div className="f20 t-color-5 p10 bold">{t('why.why_choose.card1.title')}</div>
                                <div className="f18 p10 t-color-3">{t('why.why_choose.card1.description')}</div>
                            </div>
                            {/* Card 2 */}
                            <div className="card-why t-center p20 w80">
                                <i className="fal fa-puzzle-piece f50 t-color-2 p20"></i>
                                <div className="f20 t-color-5 p10 bold">{t('why.why_choose.card2.title')}</div>
                                <div className="f18 p10 t-color-3">{t('why.why_choose.card2.description')}</div>
                            </div>
                            {/* Card 3 */}
                            <div className="card-why t-center p20 w80">
                                <i className="fal fa-users-class f50 t-color-2 p20"></i>
                                <div className="f20 t-color-5 pt10 bold">{t('why.why_choose.card3.title')}</div>
                                <div className="f18 p10 t-color-3">{t('why.why_choose.card3.description')}</div>
                            </div>
                        </div>
                        <div className="row">
                            {/* Card 4 */}
                            <div className="card-why t-center p20 w80">
                                <i className="fal fa-flag-alt f50 t-color-2 p20"></i>
                                <div className="f20 t-color-5 p10 bold">{t('why.why_choose.card4.title')}</div>
                                <div className="f18 p10 t-color-3">{t('why.why_choose.card4.description')}</div>
                            </div>
                            {/* Card 5 */}
                            <div className="card-why t-center p20 w80">
                                <i className="fal fa-handshake f50 t-color-2 p20"></i>
                                <div className="f20 t-color-5 p10 bold">{t('why.why_choose.card5.title')}</div>
                                <div className="f18 p10 t-color-3">{t('why.why_choose.card5.description')}</div>
                            </div>
                            {/* Card 6 */}
                            <div className="card-why t-center p20 w80">
                                <i className="fal fa-bullseye-arrow f50 t-color-2 p20"></i>
                                <div className="f20 t-color-5 pt10 bold">{t('why.why_choose.card6.title')}</div>
                                <div className="f18 p10 t-color-3">{t('why.why_choose.card6.description')}</div>
                            </div>
                        </div>
                    </div>
                    <div className="btn-wrap mb50">
                        <button className="btn btn-dark" onClick={() => (document as ParlaDoc).parla.popup33()}>{t('why.why_choose.book_demo')}</button>
                    </div>
                </div >
            </div >
            <Footer />
        </>
    );

}
